import { yupResolver } from '@hookform/resolvers/yup';
import {
  GetFormalOffersResponse,
  GetIwocapayPaymentLaterResponse,
} from '@iwoca/lapi-client/edge';
import { Button, Skeleton } from '@iwoca/orion';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { offerChoiceValidator } from './OfferChoiceValidator';
import { Radio } from './RadioInput/RadioInput';
import { SecurityChecksPrompt } from './SecurityChecksPrompt';
import { useDrawDownRepaymentScheduleIwocaPay } from './useDrawDownRepaymentScheduleIwocaPay';
import { useGetSecurityCheckRequirements } from './useGetSecurityCheckRequirements';
import { filterAvailableDurationsByPayLink } from './utils/filterAvailableDurationsByPaylink';
import { useGetFormalOffers } from '../../../../api/lending/lapiHooks';
import { useStateKey } from '../../../../hooks/useStateKey.hook';
import { displayCurrency } from '../../../../utils/DisplayCurrency';
import { ApprovedApplicationState } from '../../Dashboard.types';
import {
  is30DaysOffer,
  mapOfferDurationToMonths,
} from '../../utils/mapOfferDurationToMonths';

type FormValues = {
  offerId: string;
};

export const OfferChoiceForm = ({
  application,
}: {
  application: ApprovedApplicationState;
}) => {
  const { isAnyRequirementVisible } = useGetSecurityCheckRequirements();
  const { stateKey } = useStateKey();
  const { formalOffers, loadingFormalOffers } = useGetFormalOffers();
  const payLink = application.payLink;

  const initialFormValues: FormValues = {
    offerId: '',
  };
  const methods = useForm({
    defaultValues: initialFormValues,
    resolver: yupResolver(offerChoiceValidator),
    mode: 'all',
  });

  const handleSubmit = ({ offerId }: FormValues) => {
    // TODO: Refactor this to use React Router navigation instead of setting window.location.href directly
    const targetURL = new URL('/pay/checkout/', window.location.origin);

    targetURL.searchParams.append('payLinkId', payLink.id);
    targetURL.searchParams.append('offerId', offerId);

    if (stateKey) {
      targetURL.searchParams.append('state_key', stateKey);
    }

    const overlay = new URLSearchParams(window.location.search).get('overlay');
    if (overlay) {
      targetURL.searchParams.append('overlay', overlay);
    }

    window.location.href = targetURL.toString();
  };

  const onSubmit = methods.handleSubmit(handleSubmit);
  const { isValid } = methods.formState;

  if (loadingFormalOffers)
    return (
      <>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </>
    );

  if (!formalOffers) return null;

  const availableProducts = filterAvailableDurationsByPayLink({
    offers: formalOffers.formal_offers,
    payLink,
  });

  if (availableProducts.length === 1) {
    const selectedOption = methods.getValues(PAYMENT_OPTION_INPUT_ID);
    if (!selectedOption) {
      methods.setValue(PAYMENT_OPTION_INPUT_ID, availableProducts[0].offer_id!);
    }
  }

  return (
    <div>
      <FormProvider {...methods}>
        <div className="mb-m text-l font-med leading-6">
          Choose a payment option
        </div>
        <form autoComplete="off" className="flex flex-col" onSubmit={onSubmit}>
          <div className="mb-l grid gap-m">
            {availableProducts.map((offer) => (
              <OfferChoiceOption
                key={offer.offer_id}
                offer={offer}
                payLink={payLink}
              />
            ))}
          </div>
          <SecurityChecksPrompt />
          <div className="flex justify-end">
            <Button className="xs:w-full" disabled={!isValid}>
              {isAnyRequirementVisible
                ? 'Complete security checks'
                : 'Finish checkout'}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

const PAYMENT_OPTION_INPUT_ID = 'offerId' as const;

const OfferChoiceOption = ({
  offer,
  payLink,
}: {
  offer: NonNullable<GetFormalOffersResponse['data']>['formal_offers'][number];
  payLink: GetIwocapayPaymentLaterResponse['data'][number];
}) => {
  const { register, watch } = useFormContext();
  const watchPaymentOption = watch(PAYMENT_OPTION_INPUT_ID);
  const isChecked = watchPaymentOption === offer.offer_id!;
  const id = offer.offer_id!;
  const duration = mapOfferDurationToMonths(offer);

  const {
    monthlyRepayment,
    interestRate,
    loadingDrawDownRepaymentScheduleIwocapay,
  } = useDrawDownRepaymentScheduleIwocaPay({
    offer,
    payLink,
  });

  if (loadingDrawDownRepaymentScheduleIwocapay) {
    return <Skeleton />;
  }

  if (monthlyRepayment === undefined || interestRate === undefined) return null;

  const is30Days = is30DaysOffer(offer);

  return (
    <Radio
      {...register(PAYMENT_OPTION_INPUT_ID)}
      id={id}
      checked={isChecked}
      value={id}
      className="h-[25px] w-[25px] rounded-2xl"
      label={
        <label
          className="block w-auto cursor-pointer py-xl pl-3xl pr-m font-med text-secondary-20"
          htmlFor={id}
        >
          <span>
            {is30Days ? (
              <>
                <b> Pay {displayCurrency(monthlyRepayment)} </b> in 30 days time
              </>
            ) : (
              <>
                <b> Pay {displayCurrency(monthlyRepayment)} </b> per month for{' '}
                {duration} months
              </>
            )}
          </span>
          <span className="px-2 break-all"> | </span>
          <span className="whitespace-nowrap font-reg">
            {interestRate === 0
              ? 'interest-free'
              : `${(interestRate * 100).toFixed(2)}% per month`}
          </span>
        </label>
      }
      frame
    />
  );
};
